import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { loginResponseSchema } from '@castinflow/shared';
import { Alert } from '@castinflow/ui';
import { apiFetch } from '../lib/api';
import { useLinkToken } from '../lib/link-token';
import { useSession } from '../lib/session';
import { usePageMeta } from '../shell/page-meta';
import { PublicCard } from '../shell/PublicLayout';
import { tr } from '../i18n/tr';
import { LinkNotice, LinkPageTitle, NewPasswordForm, linkErrorText } from './link-page-parts';

/**
 * Davet kabulü (M7 T2) — e-postadaki davet bağlantısıyla açılan oturumsuz sayfa. Davetli parolasını belirler; sunucu daveti
 * tüketir ve oturum açar (`POST /auth/invite/accept`).
 *
 * Belirteç adresten okunur okunmaz adres çubuğundan kaldırılır (`useLinkToken`) ve yalnız bu isteğin gövdesinde, aynı
 * kökendeki API'ye gönderilir. Sunucu geçersiz, süresi dolmuş ve kullanılmış daveti ayırt etmez (tek yanıt); sayfa da üç
 * olasılığı birlikte söyler ve ne yapılacağını yazar. Oturum açıkken de açılır (yönlendirme belirteci düşürürdü).
 */
export function InviteAcceptPage() {
  usePageMeta(tr.auth.inviteTitle);
  const nav = useNavigate();
  const token = useLinkToken();
  const setMe = useSession((s) => s.setMe);
  const hasSession = useSession((s) => s.status === 'authenticated');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rejected, setRejected] = useState(false);

  async function accept(password: string) {
    if (token === null) return;
    setBusy(true);
    setError(null);
    try {
      const raw = await apiFetch<unknown>('/auth/invite/accept', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ token, password }),
      });
      const res = loginResponseSchema.parse(raw);
      if (res.status !== 'ok') {
        setError(tr.auth.linkError);
        return;
      }
      setMe(res.me);
      nav('/', { replace: true });
    } catch (e) {
      const text = linkErrorText(e);
      if (text === null) setRejected(true);
      else setError(text);
    } finally {
      setBusy(false);
    }
  }

  return (
    <PublicCard>
      <div className="space-y-4" data-testid="invite-page">
        <LinkPageTitle>{tr.auth.inviteTitle}</LinkPageTitle>
        {token === null ? (
          <LinkNotice
            testId="invite-missing"
            tone="warning"
            title={tr.auth.inviteMissingTitle}
            body={tr.auth.inviteMissingBody}
          />
        ) : rejected ? (
          <LinkNotice
            testId="invite-invalid"
            tone="danger"
            title={tr.auth.inviteInvalidTitle}
            body={tr.auth.inviteInvalidBody}
          />
        ) : (
          <NewPasswordForm
            testId="invite"
            submitLabel={tr.auth.inviteSubmit}
            busy={busy}
            error={error}
            onSubmit={(password) => {
              void accept(password);
            }}
          >
            <p className="text-base text-fg-muted">{tr.auth.inviteIntro}</p>
            {hasSession ? (
              <div data-testid="invite-session-note">
                <Alert tone="info" title={tr.auth.inviteSessionNote} />
              </div>
            ) : null}
          </NewPasswordForm>
        )}
      </div>
    </PublicCard>
  );
}
